import type { AdminAppealStatus } from '@carcheck/shared';
import Chip, { type ChipProps } from '@mui/material/Chip';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Appeal status badge chip for the moderation list + detail drawer
 * (AUT-130). Colours mirror the mockup's `badge--{warning,info,success}`
 * classes via MUI Chip `color`. Labels resolve through i18n
 * (`appeals.status.<value>`; CLAUDE.md rule #11).
 *
 * Typed as a total `Record<AdminAppealStatus, …>` over the LIVE 3-state
 * enum so a status-enum change in `@carcheck/shared` fails typecheck here
 * rather than rendering an uncoloured chip at runtime.
 *
 * Mapping (mockup-aligned):
 *   - pending      → warning   "ожидает"
 *   - under_review → info      "на рассмотрении"
 *   - resolved     → success   "решена"
 */
type ChipColor = ChipProps['color'];

const STATUS_COLOR: Record<AdminAppealStatus, ChipColor> = {
  pending: 'warning',
  under_review: 'info',
  resolved: 'success',
};

export const AppealStatusBadge = ({ status }: { status: AdminAppealStatus }): ReactElement => {
  const { t } = useTranslation();
  return <Chip size="small" color={STATUS_COLOR[status]} label={t(`appeals.status.${status}`)} />;
};
